import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material"
import { usePromptCatalog } from "@/shared/hooks/usePromptCatalog"

interface RegenerateImageDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (value: { promptId?: string; customPrompt?: string }) => Promise<void> | void
  loading?: boolean
  currentPromptText?: string
}

type PromptMode = "catalog" | "custom"

const RegenerateImageDialog: React.FC<RegenerateImageDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  currentPromptText
}) => {
  const { prompts, loading: promptsLoading, error } = usePromptCatalog()
  const [mode, setMode] = useState<PromptMode>("catalog")
  const [promptId, setPromptId] = useState("")
  const [customPrompt, setCustomPrompt] = useState("")

  const promptOptions = useMemo(
    () =>
      prompts.map(prompt => ({
        id: prompt.id,
        label: prompt.name
      })),
    [prompts]
  )

  useEffect(() => {
    if (!open) return
    setMode("catalog")
    setPromptId("")
    setCustomPrompt("")
  }, [open])

  useEffect(() => {
    if (!promptId && promptOptions[0]?.id) {
      setPromptId(promptOptions[0].id)
    }
  }, [promptId, promptOptions])

  const handleSubmit = async () => {
    if (mode === "custom") {
      await onSubmit({ customPrompt })
      return
    }

    await onSubmit({ promptId })
  }

  const isSubmitDisabled =
    loading || promptsLoading || (mode === "catalog" ? !promptId || Boolean(error) : customPrompt.trim().length === 0)

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Try a Different Prompt</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          {currentPromptText && (
            <Typography variant="body2" color="text.secondary">
              Current prompt: {currentPromptText}
            </Typography>
          )}

          <Tabs value={mode} onChange={(_event, value: PromptMode) => setMode(value)}>
            <Tab value="catalog" label="Prompt catalog" />
            <Tab value="custom" label="Custom prompt" />
          </Tabs>

          {mode === "catalog" ? (
            <FormControl fullWidth disabled={promptsLoading || Boolean(error)}>
              <InputLabel id="regenerate-prompt-select-label">Prompt</InputLabel>
              <Select
                labelId="regenerate-prompt-select-label"
                label="Prompt"
                value={promptId}
                onChange={event => setPromptId(String(event.target.value))}>
                {promptOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Custom prompt"
              multiline
              minRows={4}
              fullWidth
              value={customPrompt}
              onChange={event => setCustomPrompt(event.target.value)}
              placeholder="Describe how you'd like this image regenerated."
            />
          )}

          {promptsLoading && (
            <Stack direction="row" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading prompts…</Typography>
            </Stack>
          )}

          {error && (
            <Typography variant="body2" color="error">
              Failed to load prompts. Try again later or use a custom prompt.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitDisabled} variant="contained">
          {loading ? "Regenerating…" : "Regenerate"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RegenerateImageDialog
